import { Flex } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getContragentsSearch } from "tm-shared/api";
import { validationMessage } from "tm-shared/utils";
import { arrayToRecord } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import { PrimaryText } from "@/shared/ui/Typography";
import {
    setPointCreatePageContragentsMap,
    setPointCreatePageModal,
    setPointCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";
import { PointCreateModal } from "@/types/modals";

import { FieldName } from "../CreatePointForm.constants";

interface ContragentSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const ContragentSection = ({
    activeMap,
    resetBelow,
}: ContragentSectionProps) => {
    const dispatch = useAppDispatch();

    const { optionCache, contragentsMap } = useAppSelector(
        (s) => s.ui.pointCreatePage,
    );

    const [searchQuery, setSearchQuery] = useState("");

    const { control, setValue } = useFormContext<NewPointFormValues>();

    const region = useWatch<NewPointFormValues, "region">({
        name: "region",
        control,
    });
    const contragent = useWatch<NewPointFormValues>({
        name: "contragent",
        control,
    });

    const handleAddLegalEntity = useCallback(() => {
        dispatch(
            setPointCreatePageModal({
                type: PointCreateModal.AddContragent,
            }),
        );
    }, [dispatch]);

    const {
        options: fetchedOptions,
        loading: loadingLegalEntity,
        data: allLegalEntities,
    } = useAsyncOptions({
        fetchFn: (signal) =>
            getContragentsSearch(
                {
                    query: searchQuery,
                    mainRegionId: region,
                },
                {
                    axiosOverrides: { signal },
                },
            ),
        selectData: (response) => Object.values(response.result),
        mapToOption: (c) => {
            const alias = c.alias ? ` (${c.alias})` : "";
            const label = c.fullName + alias;

            return { label, value: c.id };
        },
        dependencies: [searchQuery, region],
        enabled:
            ((!searchQuery && !contragent) || searchQuery.length >= 3) &&
            activeMap.contragent,
        debounceMs: 500,
    });

    useEffect(() => {
        if (fetchedOptions?.length) {
            dispatch(
                setPointCreatePageOptionsCache({
                    fieldName: "contragent",
                    options: fetchedOptions,
                }),
            );

            if (allLegalEntities?.result) {
                const contragentsMap = arrayToRecord(
                    allLegalEntities.result,
                    "id",
                );

                dispatch(setPointCreatePageContragentsMap(contragentsMap));
            }
        }
    }, [allLegalEntities?.result, dispatch, fetchedOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQuery(value);
            setValue("contragent", "");
            resetBelow("contragent");
        },
        [resetBelow, setValue],
    );

    const handleAfterChange = useCallback(() => {
        resetBelow("contragent");
    }, [resetBelow]);

    const currentContragent = contragentsMap[contragent ?? ""];

    return (
        <Flex vertical gap={8}>
            <RHFAutocomplete
                name="contragent"
                options={optionCache?.["contragent"] ?? []}
                label="Юридична особа"
                rules={{
                    required: validationMessage.required,
                }}
                actionOption={{
                    label: "Додати юридичну особу",
                    onClick: handleAddLegalEntity,
                }}
                loading={loadingLegalEntity}
                allowClear={false}
                disabled={!activeMap.contragent}
                onAfterChange={handleAfterChange}
                onInputChange={handleInputChange}
                selectProps={{ size: "large" }}
            />

            {currentContragent && currentContragent.id.length <= 12 && (
                <Flex
                    vertical
                    gap={4}
                    style={{
                        opacity: activeMap.contragent ? 1 : 0.4,
                        pointerEvents: "none",
                    }}
                >
                    <Flex gap={4}>
                        <PrimaryText fontSize={12}>ІНН/ЄДРПОУ:</PrimaryText>
                        <PrimaryText>{currentContragent.id}</PrimaryText>
                    </Flex>

                    {currentContragent.ceoName && (
                        <Flex gap={4}>
                            <PrimaryText fontSize={12}>Директор:</PrimaryText>
                            <PrimaryText>
                                {currentContragent.ceoName}
                            </PrimaryText>
                        </Flex>
                    )}
                </Flex>
            )}
        </Flex>
    );
};
